import React from "react";
import styles from "./ChatDialog.module.css";
import AssetsPack from "../../../util/AssetsPack";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

export enum ChatType {
  User = "user",
  CustomerSupport = "agent",
  JokerBot = "robot",
}

export enum MessageType {
  Bad = "bad",
  Changed = "changed",
  Good = "good",
  Normal = "normal",
}

const isImageUrl = (val: unknown): val is string => {
  if (typeof val !== "string") return false;
  const s = val.trim();

  if (/^data:image\/(png|jpe?g|gif|webp|bmp|svg\+xml);base64,/i.test(s))
    return true;

  try {
    const u = new URL(s);
    if (!/^https?:$/.test(u.protocol)) return false;
    const pathname = u.pathname.toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(pathname);
  } catch {
    return false;
  }
};

export interface ChatDialogProps {
  id: number | string;
  type: ChatType;
  datetime: string;
  message: string;
  isActive?: boolean;
  isChatFailure?: boolean;
  messageType?: MessageType;
  image?: string | string[];
  replyId?: string;
  adminAction?: number;
  onClickCallback?: (id: number | string) => void;
}

const ChatDialog: React.FC<ChatDialogProps> = ({
  id,
  type,
  datetime,
  message,
  isActive = false,
  messageType = MessageType.Normal,
  isChatFailure,
  image,
  replyId,
  adminAction,
  onClickCallback,
}) => {
  const { t } = useTranslation();

  const isImg = isImageUrl(message);

  const ICONS_MAP = {
    [ChatType.User]: AssetsPack.icons.ICON_CUSTOMER.default,
    [ChatType.CustomerSupport]: AssetsPack.icons.ICON_CUSTOMER_SUPPORT.default,
    [ChatType.JokerBot]: AssetsPack.icons.ICON_ROBOT.default,
  };

  const chatUserName = {
    [ChatType.User]: t("chatDialog.jokerUser"),
    [ChatType.CustomerSupport]: t("chatDialog.customer_service"),
    [ChatType.JokerBot]: t("chatDialog.jokerBot"),
  };

  return (
    <div
      className={clsx(
        styles["chat-row"],
        styles[`chat-${type}-row`],
        isActive && styles["active-question"],
        isChatFailure && styles["failure"]
      )}
      onClick={() => {
        if (type == ChatType.JokerBot) onClickCallback?.(id);
      }}
    >
      <div
        className={clsx(styles[`chat-bubble`], isActive && styles["active"])}
      >
        <div className={styles["chat-bubble-date"]}>{datetime}</div>
        <div className={styles[`chat-message`]}>
          <div className={styles[`chat-name-avatar`]}>
            <div className={styles[`chat-avatar`]}>
              <img className={styles["image"]} src={ICONS_MAP[type]} alt="" />
            </div>
            <div>
              <p>{chatUserName[type]}</p>
              <p style={{ fontSize: "12px" }}> {`id: ${id}`}</p>
              {type == ChatType.JokerBot && (
                <p style={{ fontSize: "12px" }}>{`reply to: ${replyId}`}</p>
              )}
              {type == ChatType.CustomerSupport &&
                adminAction !== undefined && (
                  <p
                    style={{ fontSize: "12px" }}
                  >{`admin action: ${adminAction}`}</p>
                )}
            </div>
          </div>
          {isImg ? (
            <img
              className={styles["chat-image"]}
              src={message}
              alt="chat-img"
              loading="lazy"
            />
          ) : (
            <p className={clsx(styles[`${messageType}`], styles["message"])}>
              {message}
            </p>
          )}
          {(Array.isArray(image) ? image : image ? [image] : [])
            .filter(Boolean)
            .map((src, i) => (
              <img
                key={`${String(src)}_${i}`}
                className={styles["chat-image"]}
                src={String(src)}
                alt={`chat-image-${i + 1}`}
                loading="lazy"
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ChatDialog;
